import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, CalendarDays, X, UserCog, Check } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { value: "all", label: "Todas" },
  { value: "unread", label: "No leídas" },
  { value: "read", label: "Leídas" },
  { value: "managed", label: "Gestionadas" },
];

export default function NotificationsPage() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("unread");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axiosAuth().get(`/notifications/all?status=${filter}&limit=200`)
      .then(r => setItems(r.data || []))
      .catch(() => toast.error("Error cargando notificaciones"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const open = async (n) => {
    if (n.state === "unread") {
      try { await axiosAuth().post(`/notifications/${n.id}/read`); } catch {}
    }
    const url = n.action_url || n.link || "/admin";
    if (url.startsWith("http")) window.location.href = url;
    else navigate(url);
  };

  const markManaged = async (n, e) => {
    e.stopPropagation();
    try {
      await axiosAuth().post(`/notifications/${n.id}/managed`);
      toast.success("Notificación marcada como gestionada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const markAllRead = async () => {
    try {
      await axiosAuth().post("/notifications/read-all");
      toast.success("Todas las notificaciones marcadas como leídas");
      load();
    } catch {
      toast.error("Error al marcar todas");
    }
  };

  const iconFor = (t) => {
    if (t === "cancelacion_cliente") return <X className="w-4 h-4 text-red-500" />;
    if (t === "modificacion_cliente") return <CalendarDays className="w-4 h-4 text-purple-500" />;
    if (t === "portal_request") return <UserCog className="w-4 h-4 text-blue-500" />;
    return <Bell className="w-4 h-4 text-slate-500" />;
  };

  return (
    <div className="space-y-4" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase">Notificaciones</h1>
          <p className="text-sm text-slate-500 mt-1">
            Historial completo de avisos del sistema. Click para revisar la solicitud asociada.
          </p>
        </div>
        <Button onClick={markAllRead} variant="ghost" className="text-xs uppercase tracking-wider" data-testid="notif-page-mark-all">
          Marcar todas como leídas
        </Button>
      </div>

      <div className="flex gap-2" data-testid="notif-filters">
        {FILTERS.map(f => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            data-testid={`notif-filter-${f.value}`}
            className={`text-xs uppercase tracking-wider px-3 h-9 rounded-sm border ${
              filter === f.value
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-sm text-slate-400 text-center">Cargando...</p>
          ) : items.length === 0 ? (
            <p className="p-12 text-sm text-slate-400 text-center" data-testid="notif-page-empty">
              No hay notificaciones en este filtro.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map(n => (
                <li
                  key={n.id}
                  onClick={() => open(n)}
                  data-testid={`notif-row-${n.id}`}
                  className={`flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer ${
                    n.state === "unread" ? "bg-amber-50/40" : ""
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">{iconFor(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs font-semibold text-slate-900">{n.message}</p>
                      {n.state === "unread" && <Badge className="bg-red-100 text-red-700 text-[9px] rounded-sm border-red-200">Nueva</Badge>}
                      {n.state === "managed" && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] rounded-sm border-emerald-200">Gestionada</Badge>}
                      {n.state === "read" && <Badge className="bg-slate-100 text-slate-600 text-[9px] rounded-sm border-slate-200">Leída</Badge>}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {n.type}{n.action_url ? ` · ${n.action_url}` : ""}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {n.created_at ? new Date(n.created_at).toLocaleString("es-ES") : ""}
                      {n.read_at && ` · leída ${new Date(n.read_at).toLocaleString("es-ES")}`}
                    </p>
                  </div>
                  {n.state !== "managed" && (
                    <button
                      type="button"
                      onClick={(e) => markManaged(n, e)}
                      title="Marcar como gestionada"
                      data-testid={`notif-mark-managed-${n.id}`}
                      className="flex-shrink-0 text-[10px] text-emerald-700 hover:bg-emerald-50 p-1.5 rounded-sm uppercase tracking-wider font-semibold inline-flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" /> Gestionada
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
